/**
 * Illuminate 域 — React Hooks
 * 
 * 提供 mock/API 双态数据源，页面组件只依赖这些 hooks。
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { shouldUseMock } from '../../api/config';
import {
  illuminateApi,
  type HealthMetric,
  type HealthScore,
  type Report,
  type DimensionItem,
  type HealthInsight,
  type IndicatorHistory,
  type BiologicalAge,
  type WatchedMetric,
  type RecommendedIndicator,
} from './api';

// ── Mock 数据 ──

const MOCK_METRICS: Record<string, HealthMetric> = {
  '2571-8': { key: '2571-8', value: 2.3,  trend: 0.21,  unit: 'mmol/L', label: '甘油三酯',    status: 'high' },
  '2085-9': { key: '2085-9', value: 1.1,  trend: -0.08, unit: 'mmol/L', label: 'HDL胆固醇',  status: 'normal' },
  '13457-7':{ key: '13457-7',value: 3.2,  trend: 0.05,  unit: 'mmol/L', label: 'LDL胆固醇',  status: 'normal' },
  '1558-6': { key: '1558-6', value: 5.4,  trend: -0.03, unit: 'mmol/L', label: '空腹血糖',    status: 'normal' },
  '3084-1': { key: '3084-1', value: 420,  trend: 0.12,  unit: 'μmol/L', label: '尿酸',        status: 'high' },
};

const MOCK_WATCHED_METRICS: WatchedMetric[] = [
  { key: '2571-8', label: '甘油三酯', value: 2.3, unit: 'mmol/L', status: 'high', updatedAt: new Date().toISOString(), hasData: true },
  { key: '2085-9', label: 'HDL胆固醇', value: 1.1, unit: 'mmol/L', status: 'normal', updatedAt: new Date().toISOString(), hasData: true },
  { key: '13457-7', label: 'LDL胆固醇', value: 3.2, unit: 'mmol/L', status: 'normal', updatedAt: new Date().toISOString(), hasData: true },
  { key: '1558-6', label: '空腹血糖', value: 5.4, unit: 'mmol/L', status: 'normal', updatedAt: new Date().toISOString(), hasData: true },
  { key: '3084-1', label: '尿酸', value: 420, unit: 'μmol/L', status: 'high', updatedAt: new Date().toISOString(), hasData: true },
];

const MOCK_SCORE: HealthScore = {
  score: 76, overallScore: 76,
  label: '良好', levelLabel: '良好', level: 'good',
  totalIndicators: 20, totalAbnormal: 3,
  dimensions: [
    { name: '代谢', score: 72, max: 100 },
    { name: '血管', score: 68, max: 100 },
    { name: '神经', score: 85, max: 100 },
    { name: '免疫', score: 78, max: 100 },
    { name: '运动', score: 65, max: 100 },
  ],
};

const MOCK_REPORTS: Report[] = [
  { taskId: 't1', fileName: '2026年3月体检报告.pdf', fileType: 'pdf', status: 'completed', progressPercent: 100, extractedMetricsCount: 24, scoreContribution: 5, createdAt: '2026-03-10T00:00:00Z' },
  { taskId: 't2', fileName: '心血管风险评估.pdf', fileType: 'pdf', status: 'completed', progressPercent: 100, extractedMetricsCount: 18, scoreContribution: 3, createdAt: '2026-02-28T00:00:00Z' },
  { taskId: 't3', fileName: '尿酸代谢检测.pdf', fileType: 'pdf', status: 'completed', progressPercent: 100, extractedMetricsCount: 8, scoreContribution: 2, createdAt: '2026-01-15T00:00:00Z' },
];

const MOCK_DIMENSIONS: DimensionItem[] = [
  { key: 'metabolism', name: '代谢', label: '代谢', score: 65, indicators: [
    { name: '尿酸', loincCode: '3084-1', value: '520', unit: 'μmol/L', status: 'high', refRange: '150-420' },
    { name: '甘油三酯', loincCode: '2571-8', value: '2.8', unit: 'mmol/L', status: 'high', refRange: '0.56-1.7' },
  ]},
  { key: 'cardiovascular', name: '心血管', label: '心血管', score: 78, indicators: [
    { name: 'LDL胆固醇', loincCode: '13457-7', value: '3.2', unit: 'mmol/L', status: 'normal', refRange: '0-3.4' },
    { name: '血压(收缩)', loincCode: '8480-6', value: '128', unit: 'mmHg', status: 'normal', refRange: '90-140' },
  ]},
  { key: 'immune', name: '免疫', label: '免疫', score: 82, indicators: [
    { name: '白细胞', loincCode: '6690-2', value: '6.5', unit: '×10⁹/L', status: 'normal', refRange: '4.0-10.0' },
  ]},
  { key: 'neuro', name: '神经', label: '神经', score: 85, indicators: [] },
  { key: 'endocrine', name: '内分泌', label: '内分泌', score: 70, indicators: [
    { name: '空腹血糖', loincCode: '1558-6', value: '5.4', unit: 'mmol/L', status: 'normal', refRange: '3.9-6.1' },
  ]},
];

const MOCK_INSIGHT: HealthInsight = {
  id: 'mock-insight',
  content: '您的代谢维度偏低，主要因为尿酸和血脂指标异常。建议优先关注嘌呤摄入控制和有氧运动。',
  generatedAt: new Date().toISOString(),
  cached: false,
};

const MOCK_BIOLOGICAL_AGE: BiologicalAge = {
  overallAge: 32,
  chronologicalAge: 35,
  systemAges: [
    { key: 'metabolism', name: '代谢', age: 34, status: 'better', note: '尿酸略高，但血脂控制良好', confidenceRange: '±3岁', indicators: [
      { key: '3084-1', name: '尿酸', value: '520', unit: 'μmol/L', status: 'high', refRange: '150-420' },
      { key: '2571-8', name: '甘油三酯', value: '1.8', unit: 'mmol/L', status: 'normal', refRange: '0.56-1.7' },
    ]},
    { key: 'cardiovascular', name: '心血管', age: 30, status: 'better', note: '血压和心率均在理想范围', confidenceRange: '±3岁', indicators: [
      { key: '13457-7', name: 'LDL胆固醇', value: '3.2', unit: 'mmol/L', status: 'normal', refRange: '0-3.4' },
      { key: '8480-6', name: '血压(收缩)', value: '118', unit: 'mmHg', status: 'normal', refRange: '90-140' },
    ]},
    { key: 'immune', name: '免疫', age: 33, status: 'better', note: '白细胞计数正常，免疫力稳定', confidenceRange: '±3岁', indicators: [
      { key: '6690-2', name: '白细胞', value: '6.5', unit: '×10⁹/L', status: 'normal', refRange: '4.0-10.0' },
    ]},
    { key: 'neuro', name: '神经', age: 36, status: 'worse', note: '睡眠质量一般，建议改善作息', confidenceRange: '±3岁', indicators: [] },
    { key: 'endocrine', name: '内分泌', age: 31, status: 'better', note: '血糖和甲状腺功能正常', confidenceRange: '±3岁', indicators: [
      { key: '1558-6', name: '空腹血糖', value: '5.4', unit: 'mmol/L', status: 'normal', refRange: '3.9-6.1' },
    ]},
    { key: 'liver', name: '肝脏', age: 32, status: 'same', note: '肝酶指标在正常范围内', confidenceRange: '±3岁', indicators: [] },
    { key: 'kidney', name: '肾脏', age: 35, status: 'same', note: '肌酐和尿素氮无异常', confidenceRange: '±3岁', indicators: [] },
    { key: 'digestion', name: '消化', age: 33, status: 'better', note: '消化功能良好', confidenceRange: '±3岁', indicators: [] },
  ],
  improvementTargets: [
    {
      key: '3084-1',
      name: '尿酸',
      value: '520',
      unit: 'μmol/L',
      status: 'high',
      refRange: '150-420',
      direction: 'lower',
      target: '降至参考范围 150-420',
      reason: '提示嘌呤代谢和肾脏排泄负担，下降后可减轻肾脏与代谢压力。',
      systemKey: 'metabolism',
      systemName: '代谢',
    },
  ],
  reasoning: '整体代谢和心血管状态优于实际年龄，但神经系统因睡眠问题略显衰老。',
  recommendations: ['每晚固定时间入睡', '每周进行150分钟有氧运动', '定期监测尿酸水平'],
  confidenceRange: '±3岁',
  generatedAt: new Date().toISOString(),
  cached: false,
};

const MOCK_INDICATOR_HISTORY: IndicatorHistory = {
  name: '尿酸',
  dataPoints: [
    { date: '2026-03-17T00:00:00Z', value: 520, unit: 'μmol/L', status: 'high' },
    { date: '2026-02-17T00:00:00Z', value: 480, unit: 'μmol/L', status: 'high' },
    { date: '2026-01-17T00:00:00Z', value: 450, unit: 'μmol/L', status: 'high' },
    { date: '2025-12-17T00:00:00Z', value: 420, unit: 'μmol/L', status: 'normal' },
  ],
  trend: 'rising',
  changeRate: 8.3,
  interpretation: '尿酸呈上升趋势 (+8.3%)',
  totalRecords: 4,
};

// ── Session-level cache（防止 React StrictMode 双挂载重复调用昂贵 API）──

const sessionCache = new Map<string, { data: unknown; ts: number }>();
const inFlightCache = new Map<string, Promise<unknown>>();
const SESSION_TTL_MS = 5 * 60 * 1000; // 5 分钟内不重复请求

function getCached<T>(key: string): T | null {
  const entry = sessionCache.get(key);
  if (!entry) return null;
  if (Date.now() - entry.ts > SESSION_TTL_MS) { sessionCache.delete(key); return null; }
  return entry.data as T;
}

function setCached<T>(key: string, data: T) {
  sessionCache.set(key, { data, ts: Date.now() });
}

/** 清除指定 key 的 session 缓存（用于关注/取消关注后刷新首页） */
export function clearSessionCache(key: string) {
  sessionCache.delete(key);
}

// ── Hooks ──

interface DataState<T> {
  data: T | null;
  loading: boolean;
  error: string | null;
  refetch: () => void;
  isMock: boolean;
}

function useApiData<T>(mockData: T, apiFn: () => Promise<T>, domain = 'illuminate', cacheKey?: string): DataState<T> {
  const useMock = shouldUseMock(domain);
  const [data, setData] = useState<T | null>(useMock ? mockData : null);
  const [loading, setLoading] = useState(!useMock);
  const [error, setError] = useState<string | null>(null);
  const [isMock, setIsMock] = useState(useMock);

  // Ref 化，避免 inline arrow function 每次渲染创建新引用导致 useEffect 无限触发
  const apiFnRef = useRef(apiFn);
  apiFnRef.current = apiFn;
  const mockDataRef = useRef(mockData);
  mockDataRef.current = mockData;

  const doFetch = useCallback(async () => {
    if (useMock) {
      setData(mockDataRef.current);
      setIsMock(true);
      setLoading(false);
      return;
    }
    // 命中 session 缓存时直接返回，避免重复调用昂贵 API（如 LLM）
    if (cacheKey) {
      const cached = getCached<T>(cacheKey);
      if (cached !== null) {
        setData(cached);
        setIsMock(false);
        setLoading(false);
        return;
      }
    }
    setLoading(true);
    setError(null);
    try {
      let result: T;
      const inFlight = cacheKey ? inFlightCache.get(cacheKey) : null;
      if (inFlight) {
        result = await inFlight as T;
      } else {
        const request = apiFnRef.current();
        if (cacheKey) inFlightCache.set(cacheKey, request);
        try {
          result = await request;
        } finally {
          if (cacheKey) inFlightCache.delete(cacheKey);
        }
      }
      if (cacheKey) setCached(cacheKey, result);
      setData(result);
      setIsMock(false);
    } catch (e) {
      const msg = e instanceof Error ? e.message : '加载失败';
      setError(msg);
    } finally {
      setLoading(false);
    }
  }, [useMock, cacheKey]); // 不再依赖 apiFn/mockData，避免无限循环

  useEffect(() => { doFetch(); }, [doFetch]);

  return { data, loading, error, refetch: doFetch, isMock };
}

/** 健康仪表盘：指标 + 评分 + 洞察 */
export function useHealthDashboard() {
  const metrics = useApiData(
    Object.values(MOCK_METRICS),
    async () => {
      const res = await illuminateApi.getHealthData();
      return res.metrics;
    },
  );

  const score = useApiData(
    MOCK_SCORE,
    () => illuminateApi.getHealthScore(),
  );

  const insights = useApiData(
    MOCK_INSIGHT,
    () => illuminateApi.getInsights(),
    'illuminate',
    'health_insights',
  );

  return {
    metrics: metrics.data ?? [],
    score: score.data,
    insights: insights.data,
    loading: metrics.loading || score.loading,
    error: metrics.error || score.error,
    refetch: () => { metrics.refetch(); score.refetch(); insights.refetch(); },
  };
}

/** 每日健康提示 */
export function useDailyTip() {
  return useApiData(
    { content: '保持每日 7-8 小时睡眠，是代谢修复的基石。', source: 'Medicine 3.0' },
    () => illuminateApi.getDailyTip(),
  );
}

/** 报告列表 */
export function useReports() {
  return useApiData(
    MOCK_REPORTS,
    () => illuminateApi.getReports(),
    'illuminate',
    'reports_list',
  );
}

/** 8+N 维度矩阵 */
export function useDimensionMatrix() {
  return useApiData(
    MOCK_DIMENSIONS,
    async () => {
      const res = await illuminateApi.getDimensionMatrix();
      return res.dimensions;
    },
  );
}

/** 指标历史趋势（响应 since 变化，支持 tab 切换） */
export function useIndicatorHistory(name: string, since?: string) {
  const useMock = shouldUseMock('illuminate');
  const mockData: IndicatorHistory = { ...MOCK_INDICATOR_HISTORY, name: name || MOCK_INDICATOR_HISTORY.name };
  const [data, setData] = useState<IndicatorHistory | null>(useMock ? mockData : null);
  const [loading, setLoading] = useState(!useMock);
  const [isMock, setIsMock] = useState(useMock);

  useEffect(() => {
    if (useMock || !name) return;
    let cancelled = false;
    setLoading(true);
    illuminateApi.getIndicatorHistory(name, 50, since)
      .then(res => { if (!cancelled) { setData(res); setIsMock(false); } })
      .catch(() => undefined)
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [name, since, useMock]);

  return { data, loading, isMock };
}

/** 指标原始记录列表 */
export function useIndicatorObservations(loincCode: string) {
  const [data, setData] = useState<import('./api').ObservationRecord[] | null>(null);
  const [loading, setLoading] = useState(true);

  const fetch = useCallback(() => {
    if (!loincCode) return;
    setLoading(true);
    illuminateApi.getIndicatorObservations(loincCode)
      .then(res => setData(res.observations))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [loincCode]);

  useEffect(() => {
    fetch();
  }, [fetch]);

  return { data, loading, refetch: fetch };
}

/** 关注指标及其最新记录（后端直接返回，不再做前端交集过滤） */
export function useWatchedMetrics() {
  const result = useApiData(
    MOCK_WATCHED_METRICS,
    () => illuminateApi.getWatchedMetrics().then(res => res.metrics),
    'illuminate',
    'watched_metrics',
  );
  return {
    data: result.data ?? [],
    loading: result.loading,
    error: result.error,
    isEmpty: (result.data ?? []).length === 0,
    refetch: result.refetch,
    isMock: result.isMock,
  };
}

/** AI 健康洞察 */
export function useHealthInsights() {
  return useApiData(
    MOCK_INSIGHT,
    () => illuminateApi.getInsights(),
    'illuminate',
    'health_insights',
  );
}

/** 生理年龄 LLM 评估 */
export function useBiologicalAge() {
  return useApiData(
    MOCK_BIOLOGICAL_AGE,
    () => illuminateApi.getBiologicalAge(),
    'illuminate',
    'biological_age',
  );
}

/** 单指标解读 */
export function useMetricAnalysis(metric: string, value: number, unit: string, history?: { date: string; value: number; unit: string }[]) {
  const [analysis, setAnalysis] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const analyze = useCallback(async () => {
    if (shouldUseMock('illuminate')) {
      setAnalysis(`${metric} 当前值 ${value}${unit}，处于参考范围边缘。建议定期监测并结合饮食运动综合管理。`);
      return;
    }
    setLoading(true);
    setAnalysis('');
    try {
      for await (const chunk of illuminateApi.analyzeMetric(metric, value, unit, history)) {
        setAnalysis(prev => (prev ?? '') + chunk);
      }
    } catch {
      setAnalysis('暂时无法获取智能解读，请稍后重试。');
    } finally {
      setLoading(false);
    }
  }, [metric, value, unit]);

  return { analysis, loading, analyze };
}

/** 系统推荐指标（无关注指标时展示） */
export function useRecommendedIndicators() {
  const result = useApiData(
    [] as RecommendedIndicator[],
    () => illuminateApi.getRecommendedIndicators().then(res => res.recommendations),
    'illuminate',
    'recommended_indicators',
  );
  return {
    data: result.data ?? [],
    loading: result.loading,
    error: result.error,
    refetch: result.refetch,
    isMock: result.isMock,
  };
}
